// Frozen T0 shapes plus stricter UI proof requirements. Tests execute both sets.
import { z } from 'zod'
import type { M2cActionResult } from '../../api/m2c'
export const M2C_LIMITS = Object.freeze({
  limitsVersion: 1,
  maxCampaigns: 1,
  targetExecutionEnabled: false,
  maxReductionPercent: 5,
  maxReductionCents: 10,
  minBidCents: 5,
  maxSourceBidCents: 200,
  maxDailyChanges: 3,
  maxDailyCampaignChanges: 1,
  assessmentDays: 14,
  maturityBufferDays: 17,
  minRepeatDays: 31,
  maxReadbacks: 3,
  readbackWindowMs: 60_000,
  permitDays: 45,
  recoveryDays: 60,
  minVolumeRatio: 0.9,
  maxSpendRatio: 0.8,
  earlyStopVolumeRatio: 0.8,
  earlyStopProfitDeltaCents: -2_000,
  entitySyncMaxAgeHours: 24,
  metricsSyncMaxAgeHours: 36,
  searchTermsSyncMaxAgeHours: 36,
} as const)



const DAY_MS = 86_400_000
const id = z.string().min(1).max(200).refine(value => value.trim().length > 0, 'Leere ID')
const dbId = z.number().int().positive().max(2_147_483_647)
const revision = z.number().int().nonnegative().max(2_147_483_646)
const amazonId = z.string().regex(/^[1-9]\d*$/).max(32)
const cents = z.number().int().positive().max(Number.MAX_SAFE_INTEGER)
// Exact millisecond UTC form prevents sub-millisecond expiry truncation.
const timestamp = z.string().datetime({ precision: 3 })
const reason = z.string().min(1).max(2_000).refine(value => value.trim().length > 0, 'Begründung fehlt')

export const entityRefSchema = z.object({
  kind: z.enum(['keyword', 'target']), profileId: dbId,
  campaignId: dbId, campaignAmazonId: amazonId,
  adGroupId: dbId, adGroupAmazonId: amazonId,
  entityId: dbId, entityAmazonId: amazonId,
}).strict()

function checkRecoveryPair(
  value: { firstDispatchedAt: string | null; recoveryExpiresAt: string | null },
  ctx: z.RefinementCtx,
): void {
  if (value.firstDispatchedAt === null && value.recoveryExpiresAt === null) return
  if (value.firstDispatchedAt === null || value.recoveryExpiresAt === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recoveryExpiresAt'], message: 'Beide Recovery-Zeitpunkte erforderlich' })
    return
  }
  if (Date.parse(value.recoveryExpiresAt) - Date.parse(value.firstDispatchedAt) !== M2C_LIMITS.recoveryDays * DAY_MS) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recoveryExpiresAt'], message: 'Recovery: 60 Tage ab erster Sendung' })
  }
}

const actionShapeSchema = z.object({
  id, revision, entity: entityRefSchema.extend({ kind: z.literal('keyword'), profileId: z.literal(10) }), operation: z.literal('decrease_bid'),
  oldBidCents: cents, targetBidCents: cents, permitId: id, permitRevision: revision,
  settingsVersion: revision, evidenceRevision: id, businessKey: id,
  firstDispatchedAt: timestamp.nullable(), recoveryExpiresAt: timestamp.nullable(),
}).strict()

/** History retains pilot identity and direction, without today's numeric caps. */
export const historicalBidActionSchema = actionShapeSchema.superRefine((action, ctx) => {
  checkRecoveryPair(action, ctx)
  if (action.targetBidCents >= action.oldBidCents) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['targetBidCents'], message: 'Historische Action muss eine Senkung sein' })
  }
})

export const bidActionSchema = actionShapeSchema.extend({
  entity: entityRefSchema.extend({ kind: z.literal('keyword'), profileId: z.literal(10) }),
  oldBidCents: cents.min(M2C_LIMITS.minBidCents).max(M2C_LIMITS.maxSourceBidCents),
  targetBidCents: cents.min(M2C_LIMITS.minBidCents),
}).superRefine((action, ctx) => {
  checkRecoveryPair(action, ctx)
  const delta = action.oldBidCents - action.targetBidCents
  if (delta <= 0 || delta > M2C_LIMITS.maxReductionCents || delta * 100 > action.oldBidCents * M2C_LIMITS.maxReductionPercent) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['targetBidCents'], message: 'Nur Senkung um höchstens 5% und 10 Cent' })
  }
})

function checkPermitDates(startsAt: string, expiresAt: string, ctx: z.RefinementCtx, path: string): void {
  if (Date.parse(expiresAt) - Date.parse(startsAt) !== M2C_LIMITS.permitDays * DAY_MS) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: 'Permit: 45 Tage ab Start, keine Verlängerung' })
  }
}

export const pilotPermitSchema = z.object({
  id, revision, status: z.enum(['active', 'stopped']), profileId: z.literal(10),
  marketplace: z.literal('DE'), adType: z.literal('SP'), familyKey: z.literal('01_HGR'), asin: z.literal('B0DGPWWNHF'),
  campaigns: z.array(z.object({ campaignId: dbId, campaignAmazonId: amazonId }).strict()).length(M2C_LIMITS.maxCampaigns),
  operatorId: dbId, limitsVersion: z.literal(M2C_LIMITS.limitsVersion), settingsVersion: revision,
  startsAt: timestamp, expiresAt: timestamp, restoreScope: z.literal('action_baseline_owner_approval'),
}).strict().superRefine((permit, ctx) => checkPermitDates(permit.startsAt, permit.expiresAt, ctx, 'expiresAt'))

/** An expiry is not owner approval. Persisted approvals remain behind the port. */
export const recoveryAuthoritySchema = z.object({
  actionId: id, permitId: id, permitStartsAt: timestamp, permitExpiresAt: timestamp,
  firstDispatchedAt: timestamp, recoveryExpiresAt: timestamp,
}).strict().superRefine((authority, ctx) => {
  checkPermitDates(authority.permitStartsAt, authority.permitExpiresAt, ctx, 'permitExpiresAt')
  checkRecoveryPair(authority, ctx)
  const first = Date.parse(authority.firstDispatchedAt)
  if (first < Date.parse(authority.permitStartsAt) || first >= Date.parse(authority.permitExpiresAt)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['firstDispatchedAt'], message: 'Erste Sendung muss im Permit liegen' })
  }
})

/** Reserved/sent/unknown writes count; transaction enforcement belongs to T3. */
export const dailyQuotaSchema = z.object({
  berlinDate: z.string().date(),
  pilotChanges: z.number().int().nonnegative().max(M2C_LIMITS.maxDailyChanges),
  campaignChanges: z.number().int().nonnegative().max(M2C_LIMITS.maxDailyCampaignChanges),
}).strict().refine(quota => quota.campaignChanges <= quota.pilotChanges, { path: ['campaignChanges'], message: 'Kampagnenzahl über Pilotzahl' })

export const readEvidenceSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('found'), entity: entityRefSchema, bidCents: cents, observedAt: timestamp }).strict(),
  z.object({ status: z.literal('not_found'), entity: entityRefSchema, observedAt: timestamp }).strict(),
  z.object({ status: z.literal('identity_mismatch'), requestedEntity: entityRefSchema, observedEntity: entityRefSchema, observedAt: timestamp }).strict(),
  z.object({ status: z.literal('read_error'), entity: entityRefSchema, reason, observedAt: timestamp }).strict(),
]).superRefine((evidence, ctx) => {
  if (evidence.status === 'identity_mismatch' && sameEntity(evidence.requestedEntity, evidence.observedEntity)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['observedEntity'], message: 'Identity mismatch benötigt unterschiedliche Entities' })
  }
})

/** confirmed means a validated single-item ACK, not readback or business success. */
export const dispatchOutcomeSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('confirmed'), entity: entityRefSchema, acknowledgedAt: timestamp }).strict(),
  z.object({ status: z.literal('rejected_pre_dispatch'), reason }).strict(),
  z.object({ status: z.literal('rejected'), reason, pilotStopped: z.literal(true) }).strict(),
  z.object({ status: z.literal('unknown'), reason, pilotStopped: z.literal(true) }).strict(),
])

export const actionStateSchema = z.enum([
  'proposed', 'blocked', 'cancelled', 'reserved', 'dispatching', 'ack_received', 'reconcile_required',
  'readback_verified', 'observing', 'evaluation_due', 'evaluated_success', 'evaluated_failure',
  'insufficient_data', 'contaminated', 'restore_requested', 'restoring', 'restored', 'restore_unresolved',
])

const resultShapeSchema = z.object({
  action: historicalBidActionSchema, state: actionStateSchema,
  outcome: dispatchOutcomeSchema.nullable(), readEvidence: readEvidenceSchema.nullable(),
}).strict()

function sameEntity(left: z.infer<typeof entityRefSchema>, right: z.infer<typeof entityRefSchema>): boolean {
  const keys = ['kind', 'profileId', 'campaignId', 'campaignAmazonId', 'adGroupId', 'adGroupAmazonId', 'entityId', 'entityAmazonId'] as const
  return keys.every(key => left[key] === right[key])
}

/** Exact evidence can coexist with the historical dispatch outcome Unknown.
 * Decrease proof: target in a verified/assessment phase; restore proof: baseline
 * in restored. Server reconcile results (T4b reconcile.ts) are proof only in the
 * shape the server can produce them: accept_not_applied → blocked with a baseline
 * read, close_superseded → contaminated with not_found or a third bid, both read
 * no earlier than the server grace after the first dispatch.
 * Latch release additionally binds proof to the local operation in api/m2c.
 */
const DECREASE_PROVEN_STATES: ReadonlySet<string> = new Set([
  'readback_verified', 'observing', 'evaluation_due', 'evaluated_success', 'evaluated_failure', 'insufficient_data', 'contaminated',
])
/** Lower bound of the server reconcile grace (reconcileGraceEnd: intent TTL 12 min + 2 min
 * buffer after the authorization anchor, which is never before firstDispatchedAt). Used only to
 * recognise the shape of a server reconcile result (a baseline read after the grace is
 * accept_not_applied; the same read inside it is an operator assertion keeping the old read).
 * It never releases a latch by elapsed time (api/m2c m2cHasLatchProof). */
export const M2C_RECONCILE_GRACE_FLOOR_MS = 14 * 60_000
type ResultShape = M2cActionResult

/** A read bound to this action's entity and dispatch anchor; null when it proves nothing. */
function anchoredRead(result: ResultShape): { at: number; bidCents: number | null } | null {
  const evidence = result.readEvidence
  if ((evidence?.status !== 'found' && evidence?.status !== 'not_found') || !sameEntity(result.action.entity, evidence.entity) ||
    !result.action.firstDispatchedAt || !result.action.recoveryExpiresAt) return null
  const at = Date.parse(evidence.observedAt)
  if (at < Date.parse(result.action.firstDispatchedAt)) return null
  if (result.outcome?.status === 'rejected' || result.outcome?.status === 'rejected_pre_dispatch') return null
  if (result.outcome?.status === 'confirmed' && (!sameEntity(result.action.entity, result.outcome.entity) ||
    at < Date.parse(result.outcome.acknowledgedAt))) return null
  return { at, bidCents: evidence.status === 'found' ? evidence.bidCents : null }
}
function afterGrace(result: ResultShape, at: number): boolean {
  return result.action.firstDispatchedAt !== null && at >= Date.parse(result.action.firstDispatchedAt) + M2C_RECONCILE_GRACE_FLOOR_MS
}

export function m2cHasResolutionProof(result: ResultShape): boolean {
  const read = anchoredRead(result)
  if (!read || read.bidCents === null) return false
  if (DECREASE_PROVEN_STATES.has(result.state)) return read.bidCents === result.action.targetBidCents
  if (result.state === 'restored') return read.bidCents === result.action.oldBidCents
  return false
}

/** accept_not_applied: server read the baseline after the grace (two stable reads server-side). */
export function m2cHasNotAppliedProof(result: ResultShape): boolean {
  const read = anchoredRead(result)
  return result.state === 'blocked' && read !== null && read.bidCents === result.action.oldBidCents && afterGrace(result, read.at)
}

/**
 * Restore accept_not_applied (reconcile.ts RESTORE_STATE_AFTER): blocked with a read of the
 * unchanged decreased bid. The DTO cannot tell it from an operator assertion that kept an
 * older read, so it is proof only together with this session's own verified reconcile.
 */
export function m2cHasRestoreNotAppliedProof(result: ResultShape): boolean {
  const read = anchoredRead(result)
  return result.state === 'blocked' && read !== null && read.bidCents === result.action.targetBidCents
}

/** close_superseded: entity gone or a third bid, read after the grace. */
export function m2cHasSupersededProof(result: ResultShape): boolean {
  const read = anchoredRead(result)
  return result.state === 'contaminated' && read !== null && afterGrace(result, read.at) &&
    (read.bidCents === null || (read.bidCents !== result.action.oldBidCents && read.bidCents !== result.action.targetBidCents))
}

/** Amazon rejected the dispatch: definitive non-application, the server stopped the pilot. */
export function m2cHasRejectedProof(result: ResultShape): boolean {
  return result.state === 'blocked' && result.outcome?.status === 'rejected' &&
    result.action.firstDispatchedAt !== null && result.action.recoveryExpiresAt !== null
}

export function m2cHasPreDispatchProof(result: ResultShape): boolean {
  return ['blocked', 'cancelled'].includes(result.state) && result.outcome?.status === 'rejected_pre_dispatch' &&
    result.readEvidence === null && result.action.firstDispatchedAt === null && result.action.recoveryExpiresAt === null
}

/** System reconcile outcome the server can produce (operator_asserted is none of these). */
export function m2cHasReconcileResolutionProof(result: ResultShape): boolean {
  return m2cHasResolutionProof(result) || m2cHasNotAppliedProof(result) || m2cHasSupersededProof(result)
}

function checkResultProvenance(result: z.infer<typeof resultShapeSchema>, ctx: z.RefinementCtx): void {
  if ((DECREASE_PROVEN_STATES.has(result.state) || result.state === 'restored') && !m2cHasResolutionProof(result) &&
    !(result.state === 'contaminated' && m2cHasSupersededProof(result))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['readEvidence'], message: 'Ergebnis unbekannt: passender Readback mit exaktem Gebot fehlt oder widerspricht dem Ergebnis' })
  }
  const sent = !['proposed', 'blocked', 'cancelled', 'reserved'].includes(result.state) ||
    (result.outcome !== null && result.outcome.status !== 'rejected_pre_dispatch')
  if (sent && (result.action.firstDispatchedAt === null || result.action.recoveryExpiresAt === null)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['action', 'firstDispatchedAt'], message: 'Dispatch benötigt dauerhaft gespeicherten Recovery-Anker' })
  }
  if (result.outcome?.status === 'confirmed' && !sameEntity(result.action.entity, result.outcome.entity)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['outcome', 'entity'], message: 'ACK gehört zu anderer Entity' })
  }
  if (result.readEvidence !== null) {
    const ref = result.readEvidence.status === 'identity_mismatch' ? result.readEvidence.requestedEntity : result.readEvidence.entity
    if (!sameEntity(result.action.entity, ref)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['readEvidence'], message: 'Readback gehört zu anderer Entity' })
    }
  }
}

/** Restore/history can retain their original limits; a new run cannot. */
export const actionResultSchema = resultShapeSchema.superRefine(checkResultProvenance)
export const forwardActionResultSchema = resultShapeSchema.extend({ action: bidActionSchema }).superRefine(checkResultProvenance)

export const m2cLimitsSchema = z.object({
  limitsVersion: z.literal(M2C_LIMITS.limitsVersion), maxCampaigns: z.literal(M2C_LIMITS.maxCampaigns),
  targetExecutionEnabled: z.literal(M2C_LIMITS.targetExecutionEnabled),
  maxReductionPercent: z.literal(M2C_LIMITS.maxReductionPercent), maxReductionCents: z.literal(M2C_LIMITS.maxReductionCents),
  minBidCents: z.literal(M2C_LIMITS.minBidCents), maxSourceBidCents: z.literal(M2C_LIMITS.maxSourceBidCents),
  maxDailyChanges: z.literal(M2C_LIMITS.maxDailyChanges), maxDailyCampaignChanges: z.literal(M2C_LIMITS.maxDailyCampaignChanges),
  assessmentDays: z.literal(M2C_LIMITS.assessmentDays), maturityBufferDays: z.literal(M2C_LIMITS.maturityBufferDays),
  minRepeatDays: z.literal(M2C_LIMITS.minRepeatDays), maxReadbacks: z.literal(M2C_LIMITS.maxReadbacks),
  readbackWindowMs: z.literal(M2C_LIMITS.readbackWindowMs), permitDays: z.literal(M2C_LIMITS.permitDays), recoveryDays: z.literal(M2C_LIMITS.recoveryDays),
  minVolumeRatio: z.literal(M2C_LIMITS.minVolumeRatio), maxSpendRatio: z.literal(M2C_LIMITS.maxSpendRatio),
  earlyStopVolumeRatio: z.literal(M2C_LIMITS.earlyStopVolumeRatio), earlyStopProfitDeltaCents: z.literal(M2C_LIMITS.earlyStopProfitDeltaCents),
  entitySyncMaxAgeHours: z.literal(M2C_LIMITS.entitySyncMaxAgeHours), metricsSyncMaxAgeHours: z.literal(M2C_LIMITS.metricsSyncMaxAgeHours),
  searchTermsSyncMaxAgeHours: z.literal(M2C_LIMITS.searchTermsSyncMaxAgeHours),
}).strict()

const permitRequestSchema = z.object({ permitId: id, expectedRevision: revision }).strict()
const actionRequestSchema = z.object({ actionId: id, expectedRevision: revision }).strict()
export const previewRequestSchema = permitRequestSchema
export const previewResponseSchema = z.object({
  permit: pilotPermitSchema, actions: z.array(bidActionSchema), limits: m2cLimitsSchema, blockers: z.array(reason),
  /** N1: candidates blocked only by their own facts; they do not block the run. */
  blockedCandidates: z.array(z.object({ entity: entityRefSchema, reasons: z.array(reason).min(1) }).strict()),
}).strict().superRefine((preview, ctx) => {
  const campaign = preview.permit.campaigns[0]
  preview.actions.forEach((action, index) => {
    if (action.permitId !== preview.permit.id || action.permitRevision !== preview.permit.revision ||
      action.settingsVersion !== preview.permit.settingsVersion || !campaign ||
      action.entity.profileId !== preview.permit.profileId || action.entity.campaignId !== campaign.campaignId ||
      action.entity.campaignAmazonId !== campaign.campaignAmazonId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['actions', index], message: 'Action passt nicht zu Permit/Revision/Scope' })
    }
  })
})
export const runRequestSchema = permitRequestSchema.extend({
  actions: z.array(actionRequestSchema).min(1).max(M2C_LIMITS.maxDailyCampaignChanges), requestKey: id,
})
export const runResponseSchema = z.object({
  runId: id, permitId: id, pilotStatus: z.enum(['active', 'stopped']),
  results: z.array(forwardActionResultSchema).min(1).max(M2C_LIMITS.maxDailyCampaignChanges),
}).strict().superRefine((run, ctx) => {
  if (run.pilotStatus !== 'stopped' && run.results.some(result => result.outcome?.status === 'unknown' || result.outcome?.status === 'rejected')) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pilotStatus'], message: 'Unklarer/abgelehnter Dispatch stoppt den Pilot' })
  }
  run.results.forEach((result, index) => {
    if (result.action.permitId !== run.permitId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['results', index, 'action', 'permitId'], message: 'Action gehört zu anderem Permit' })
    }
  })
})
export const stopRequestSchema = permitRequestSchema.extend({ reason })
export const stopResponseSchema = z.object({ permitId: id, revision, status: z.literal('stopped') }).strict()
export const historyQuerySchema = z.object({
  permitId: id, limit: z.coerce.number().int().min(1).max(100).default(20), offset: z.coerce.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).default(0),
}).strict()
export const historyResponseSchema = z.object({ actions: z.array(actionResultSchema), total: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER) }).strict()
export const reconcileRequestSchema = actionRequestSchema.extend({
  note: reason, resolution: z.enum(['readback', 'accept_not_applied', 'close_superseded', 'operator_asserted']),
})
export const reconcileResponseSchema = actionResultSchema
export const restoreRequestSchema = actionRequestSchema.extend({ ownerApprovalId: id })
export const restoreResponseSchema = actionResultSchema
